"use client";

import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { fetchDesignIdeaImagesClient } from "../_api/designIdeaImages";
import { useInfiniteScroll } from "./useInfiniteScroll";
import { useDesignIdeaImagesStore } from "../../_store/designIdeaImagesStore";

/**
 * Infinite-scroll image feed for one design-idea tag on the category page.
 * `tagId: null` (slug not in DESIGN_IDEA_TAGS) skips fetching — the empty
 * state is rendered instead, see CategoryGalleryFeedSection.
 */
export function useGalleryFeed({
  category,
  tagId,
  initialData
} = {}) {
  const enabled = tagId !== null;
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError
  } = useInfiniteQuery({
    queryKey: ["fevicol-inhomz-design-idea-images", tagId],
    queryFn: ({
      pageParam
    }) => fetchDesignIdeaImagesClient({
      tagId,
      offset: pageParam
    }),
    getNextPageParam: lastPage => lastPage.nextOffset,
    initialPageParam: 0,
    initialData: initialData ? {
      pages: [initialData],
      pageParams: [0]
    } : undefined,
    enabled,
    staleTime: 5 * 60 * 1000
  });
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const loadMoreRef = useInfiniteScroll(handleLoadMore, hasNextPage, isFetchingNextPage);
  const items = data?.pages?.flatMap(page => page.items) || [];
  const setImages = useDesignIdeaImagesStore(state => state.setImages);
  useEffect(() => {
    if (items.length) setImages(category, items);
    // `items` is rebuilt every render; its length changes only when a page lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, items.length, setImages]);
  return {
    items,
    isLoading: enabled && isLoading,
    isFetchingNextPage,
    hasNextPage,
    isError,
    loadMoreRef
  };
}